import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { profileService } from './profileService';
import challengeService from './challengeService';

type ArticleComment = Database['public']['Tables']['article_comments']['Row'];
type ArticleCommentInsert = Database['public']['Tables']['article_comments']['Insert'];

// Export the Comment type that CommentSection expects
export interface Comment extends ArticleComment {
  profiles?: {
    id: string;
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
    avatar_background: string;
  } | null;
}

// Export individual functions that CommentSection expects
export const getArticleComments = async (articleId: string): Promise<Comment[]> => {
  const { data, error } = await supabase
    .from('article_comments')
    .select(`
      *,
      user_profiles (
        id,
        username,
        full_name,
        avatar_url,
        avatar_background
      )
    `)
    .eq('article_id', articleId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching comments:', error);
    return [];
  }

  // Transform the data to match the Comment interface
  return (data || []).map(comment => {
    const profileData = comment.user_profiles;
    // Properly handle the profile data with type safety
    const userProfile = profileData && typeof profileData === 'object' && !Array.isArray(profileData) && !('error' in profileData)
      ? profileData as Comment['profiles']
      : null;

    return {
      ...comment,
      profiles: userProfile
    };
  });
};

export const addComment = async (articleId: string, userId: string, content: string): Promise<Comment> => {
  // Ensure user profile exists
  await ensureUserProfile(userId);

  const { data, error } = await supabase
    .from('article_comments')
    .insert({
      article_id: articleId,
      user_id: userId,
      content: content.trim()
    })
    .select(`
      *,
      user_profiles (
        id,
        username,
        full_name,
        avatar_url,
        avatar_background
      )
    `)
    .single();

  if (error) {
    console.error('Error adding comment:', error);
    throw error;
  }

  // Award points for commenting
  await awardPointsForComment(userId, data.id);
  
  // Update challenge progress
  await challengeService.updateChallengeProgress('comment_article', 1);

  // Transform the data to match the Comment interface
  const profileData = data.user_profiles;
  const userProfile = profileData && typeof profileData === 'object' && !Array.isArray(profileData) && !('error' in profileData)
    ? profileData as Comment['profiles']
    : null;

  return {
    ...data,
    profiles: userProfile
  };
};

export const deleteComment = async (commentId: string): Promise<void> => {
  const { error } = await supabase
    .from('article_comments')
    .delete()
    .eq('id', commentId);

  if (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

// Helper functions
const ensureUserProfile = async (userId: string) => {
  const { data: existingProfile } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('id', userId)
    .single();

  if (!existingProfile) {
    await supabase
      .from('user_profiles')
      .insert({
        id: userId,
        username: `user_${userId.substring(0, 8)}`,
        full_name: 'User'
      });
  }
};

const awardPointsForComment = async (userId: string, commentId: string) => {
  await profileService.addXP(10, 'comment_created', { commentId }, 'article_comments', commentId);
};

// Keep the original commentService export for backward compatibility
export const commentService = {
  getComments: getArticleComments,
  addComment: async (articleId: string, content: string): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    try {
      await addComment(articleId, user.id, content);
      return true;
    } catch (error) {
      return false;
    }
  },
  ensureUserProfile,
  awardPointsForComment
};
